// src/components/FlipCard/FlipCard.jsx
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Button } from 'react-bootstrap';
import { RotateCw } from 'lucide-react';
import './FlipCard.css';

/**
 * FlipCard - A reusable 3D flip card component
 * 
 * @param {boolean} flippable - Whether the card can be flipped
 * @param {ReactNode} front - Content for the front of the card
 * @param {ReactNode} back - Content for the back of the card
 * @param {ReactNode} logo - Logo element to display in footer
 * @param {ReactNode} shareButton - Share button element to display in footer
 * @param {Function} onFlip - Callback when card is flipped
 * @param {boolean} isDragging - Whether the card is being dragged
 * @param {boolean} showFlipButton - Whether to show the flip button below the card
 */
const FlipCard = forwardRef(({
  flippable = true,
  front,
  back,
  logo,
  shareButton,
  onFlip,
  isDragging = false,
  showFlipButton = false,
  // false when the host renders its own visible flip control: the card still
  // flips on a tap, but is not a second, long-named button for the keyboard
  // and screen readers doing what that control does.
  keyboardFlip = true
}, ref) => {
  const [flipped, setFlipped] = useState(false);

  // Expose flip() and reset() to parent components
  useImperativeHandle(ref, () => ({
    flip: () => {
      if (!flippable) return;
      setFlipped((f) => {
        const next = !f;
        if (onFlip) onFlip(next);
        return next;
      });
    },
    reset: () => setFlipped(false),
  }));

  const handleClick = () => {
    if (!flippable || isDragging) return;
    setFlipped((f) => {
      const next = !f;
      if (onFlip) onFlip(next);
      return next;
    });
  };

  const handleFlipButtonClick = (e) => {
    e.stopPropagation();
    handleClick();
  };

  // Unified footer component
  const Footer = () => (
    <div className="flip-card-footer">
      <div className="flip-card-footer-logo">
        {logo}
      </div>
      {shareButton && (
        <div className="flip-card-footer-share" onClick={(e) => e.stopPropagation()}>
          {shareButton}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        className={`flip-card${!flippable ? ' no-flip' : ''}${flipped ? ' flipped' : ''}`}
        onClick={handleClick}
        // Only a card that actually flips is a button. An unflippable card
        // (SchedulesHome's read-only rows, decks mid-drag) with role=button
        // put a dead focusable in the tab order, announced with the whole
        // card's text as its name and doing nothing on Enter.
        role={flippable && keyboardFlip ? 'button' : undefined}
        tabIndex={flippable && keyboardFlip ? 0 : undefined}
        onKeyDown={
          flippable && keyboardFlip
            ? (e) => {
                if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleClick();
                }
              }
            : undefined
        }
      >
        <div className="flip-card-inner">
          {/* Each away face is inert: backface-visibility only hides the
              PAINT, so once a face contains real buttons (the anonymous card
              builder's edit rows) keyboard focus could tab into invisible
              controls. inert removes the hidden face from focus/click order
              without touching the flip animation. */}
          {/* Front Face */}
          <div className="flip-card-face flip-card-front" inert={flipped ? '' : undefined}>
            <div className="flip-card-content">
              {front}
            </div>
            <Footer />
          </div>

          {/* Back Face */}
          <div className="flip-card-face flip-card-back" inert={!flipped ? '' : undefined}>
            <div className="flip-card-content">
              {back}
            </div>
            <Footer />
          </div>
        </div>
      </div>

      {/* Optional flip button below card */}
      {showFlipButton && flippable && (
        <div className="flip-card-button-container">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleFlipButtonClick}
            className="flip-card-button"
          >
            <RotateCw size={16} />
            <span>Flip Card</span>
          </Button>
        </div>
      )}
    </>
  );
});

export default FlipCard;
